<section
  class="step-notifications"
  [attr.aria-labelledby]="instanceId + '-title'"
  data-testid="step-notifications"
>
  <header class="step-notifications__header">
    <h3 #editorHeading tabindex="-1" [id]="instanceId + '-title'">
      Notifications
    </h3>
    <span class="step-notifications__delivery" role="status"
      >Delivery not connected</span
    >
  </header>
  <p class="step-notifications__boundary">
    {{
      canAuthor
        ? "Choose an event, then configure its message and recipients in the draft."
        : "Choose an event to inspect its message and recipients saved in this workflow version."
    }}
    These settings do not send messages yet.
  </p>
  <p
    *ngIf="stepWarning as warning"
    class="step-notifications__error"
    role="alert"
  >
    {{ warning }}
  </p>
  <div
    *ngIf="localError() as error"
    class="step-notifications__error"
    role="alert"
  >
    {{ error }}
    <button
      uiButton
      type="button"
      [disabled]="interactionBlocked"
      (click)="resetPendingChanges()"
    >
      Reset notification input
    </button>
  </div>
  <p
    *ngIf="selectionWarning() as warning"
    class="step-notifications__error"
    role="status"
  >
    {{ warning }}
  </p>
  <section
    *ngIf="usesDefaultLifecycle"
    class="step-notifications__defaults"
    [attr.aria-labelledby]="instanceId + '-defaults-title'"
  >
    <strong [id]="instanceId + '-defaults-title'"
      >Platform default lifecycle is active</strong
    >
    <p>
      Starting is automatic. The default completion status change is In progress
      → Completed; requirements, access and runtime checks still apply. There is
      no explicit status rule to hold notification recipients.
    </p>
    <p>
      To configure status-change recipients, review Status rules. Adding the
      first explicit rule replaces platform defaults; review the lifecycle
      impact there before applying it.
    </p>
    <button
      uiButton
      [text]="true"
      [outlined]="false"
      type="button"
      [disabled]="interactionBlocked"
      (click)="requestOwner('transitions')"
    >
      Review status rules
    </button>
  </section>
  <div
    *ngIf="!stepWarning && events.length; else noEvents"
    class="step-notifications__workspace"
  >
    <nav class="step-notifications__events" aria-label="Notification events">
      <h4 class="step-notifications__events-title">Choose an event</h4>
      <ng-container *ngFor="let group of eventGroups; trackBy: trackEventGroup">
        <div class="step-notifications__event-group">
          <h5>{{ group.label }}</h5>
          <span *ngIf="group.supportLabel">{{ group.supportLabel }}</span>
        </div>
        <button
          uiButton
          [wrap]="true"
          *ngFor="let event of group.events; trackBy: trackEvent"
          type="button"
          [attr.aria-current]="
            event.identity === selectedEvent()?.identity &&
            event.key === selectedEvent()?.key
              ? 'true'
              : null
          "
          [attr.aria-label]="
            eventTrigger(event) +
            ': ' +
            event.label +
            '. Scope: ' +
            eventScope(event) +
            '. ' +
            recipientSummary(event) +
            (event.problem ? '. Needs repair' : '')
          "
          [disabled]="interactionBlocked"
          [class.step-notifications__event--invalid]="event.problem"
          (click)="selectEvent(event, $event)"
        >
          <span>{{ eventTrigger(event) }}</span
          ><strong>{{ event.label }}</strong>
          <small
            >{{ recipientSummary(event)
            }}<ng-container *ngIf="event.problem">
              · Needs repair</ng-container
            ></small
          >
        </button>
      </ng-container>
      <div class="step-notifications__owner-actions">
        <button
          uiButton
          [text]="true"
          [outlined]="false"
          type="button"
          [disabled]="interactionBlocked"
          (click)="requestOwner('transitions')"
        >
          Status rules
        </button>
        <button
          uiButton
          [text]="true"
          [outlined]="false"
          type="button"
          [disabled]="interactionBlocked"
          (click)="requestOwner('decisions')"
        >
          Decision checks
        </button>
      </div>
    </nav>
    <section
      *ngIf="selectedEvent() as event"
      class="step-notifications__recipient-editor"
      [attr.aria-label]="event.label + ' notification'"
    >
      <header>
        <div class="step-notifications__event-heading">
          <h4 #recipientHeading tabindex="-1">{{ eventTrigger(event) }}</h4>
          <span>{{ event.label }}</span>
        </div>
        <button
          uiButton
          type="button"
          class="step-notifications__edit-owner"
          [disabled]="
            interactionBlocked || !event.owner || !!event.ownerProblem
          "
          (click)="requestSelectedOwner()"
        >
          {{ canAuthor ? "Edit" : "Inspect" }}
          {{ event.kind === "decision" ? "decision" : "status change" }}
        </button>
      </header>
      <dl class="step-notifications__event-summary">
        <div>
          <dt>Scope</dt>
          <dd>{{ eventScope(event) }}</dd>
        </div>
        <div>
          <dt>Event owner</dt>
          <dd>{{ eventOwner(event) }}</dd>
        </div>
        <div>
          <dt>Configured recipients</dt>
          <dd>{{ recipientSummary(event) }}</dd>
        </div>
      </dl>
      <p
        *ngIf="event.supportedCompletion === false && !event.ownerProblem"
        class="step-notifications__retained"
      >
        This saved status pair is retained for inspection. It does not provide a
        staff action; its recipients do not make the status change available.
      </p>
      <p *ngIf="event.problem" class="step-notifications__error" role="alert">
        {{ event.problem }}
      </p>
      <p
        *ngIf="messageFormatNotice as notice"
        class="step-notifications__retained"
        role="status"
      >
        {{ notice }}
      </p>
      <phms-workflow-notification-message-editor
        *ngIf="event.owner"
        [message]="event.owner.notificationMessage ?? null"
        [contextProfiles]="notificationContextProfiles"
        [subjectTypeCode]="document?.subjectTypeCode ?? ''"
        [eventKind]="event.kind"
        [canAuthor]="canEditMessages"
        [inspectionMode]="!canAuthor"
        [interactionBlocked]="
          interactionBlocked || !!event.problem || !!localError()
        "
        [referenceLoading]="referenceLoading"
        [instanceId]="instanceId + '-message'"
        [ownerKey]="selectedOwnerKey"
        [resetVersion]="resetVersion"
        (messageChange)="applyMessage($event)"
        (pendingChangesChange)="onMessagePendingChange($event)"
      />
      <div
        *ngIf="
          canAuthor && !event.ownerProblem && invalidRecipientIndexes.length
        "
        class="step-notifications__repairs"
      >
        <button
          uiButton
          severity="danger"
          [text]="true"
          [outlined]="false"
          *ngFor="let index of invalidRecipientIndexes"
          type="button"
          [disabled]="
            interactionBlocked || referenceLoading || hasPendingChanges()
          "
          (click)="removeInvalidRecipient(index)"
        >
          Remove invalid recipient {{ index + 1 }}
        </button>
      </div>
      <h5 *ngIf="event.owner">Recipients</h5>
      <phms-workflow-notification-rule-editor
        *ngIf="event.owner"
        [embeddedContext]="true"
        [rules]="event.owner.notifications ?? null"
        [canAuthor]="canEditSelection"
        [inspectionMode]="!canAuthor"
        [interactionBlocked]="
          interactionBlocked || !!event.problem || !!localError()
        "
        [referenceLoading]="referenceLoading"
        [roleCodes]="roleCodes"
        [queueCodes]="queueCodes"
        [managerTargetCodes]="managerNotificationTargetCodes"
        [externalTargetCodes]="externalNotificationTargetCodes"
        [triggerLabel]="event.triggerLabel"
        [instanceId]="instanceId + '-recipients'"
        [ownerKey]="selectedOwnerKey"
        [resetVersion]="resetVersion"
        (rulesChange)="applyNotifications($event)"
        (pendingChangesChange)="onPendingChange($event)"
      />
    </section>
  </div>
  <ng-template #noEvents>
    <div *ngIf="!stepWarning" class="step-notifications__empty">
      <strong>No explicit notification events saved</strong>
      <p>
        A message and its recipients attach to an explicit status rule or
        decision check. Review the owning configuration before creating an event
        for notifications.
      </p>
      <div class="step-notifications__owner-actions">
        <button
          uiButton
          [text]="true"
          [outlined]="false"
          *ngIf="!usesDefaultLifecycle"
          type="button"
          [disabled]="interactionBlocked"
          (click)="requestOwner('transitions')"
        >
          Review status rules
        </button>
        <button
          uiButton
          [text]="true"
          [outlined]="false"
          type="button"
          [disabled]="interactionBlocked"
          (click)="requestOwner('decisions')"
        >
          Review decision checks
        </button>
      </div>
    </div>
  </ng-template>
</section>
